import { ConfigProvider, Segmented } from 'antd';
import TermTooltip from 'components/common/TermTooltip/TermTooltip';
import ViewHeader from 'components/common/ViewHeader/ViewHeader';
import { FARMS_URL_PARAM_DIAGNOSTICS_LENS } from 'constants/routeConstants';
import {
  FARM_DIAGNOSTICS_LENS_BIO_VS_FINANCIAL,
  FARM_DIAGNOSTICS_LENS_FINANCIAL_EFFICIENCY,
  FARM_DIAGNOSTICS_LENS_LABELS,
  FARM_DIAGNOSTICS_LENS_ROTATION_EFFICIENCY,
  FARM_DIAGNOSTICS_LENS_WUE_TREND,
  FARM_DIAGNOSTICS_NO_DATA_MESSAGE,
  FARM_DIAGNOSTICS_NO_PADDOCK_MESSAGE,
  FARM_DIAGNOSTICS_RUE_TOOLTIP,
  VIEW_PURPOSE_DIAGNOSTICS_BIO_VS_FINANCIAL,
  VIEW_PURPOSE_DIAGNOSTICS_FINANCIAL_EFFICIENCY,
  VIEW_PURPOSE_DIAGNOSTICS_ROTATION_EFFICIENCY,
  VIEW_PURPOSE_DIAGNOSTICS_WUE_TREND,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IFarm } from 'interfaces/IFarm';
import type { IInput } from 'interfaces/IInput';
import { InputTaskCategory } from 'interfaces/IInputTask';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { RootState } from 'redux/Store';
import {
  setFarmDiagnosticsCategory,
  setFarmDiagnosticsTimeRange,
  type FarmDiagnosticsLens,
} from '../../../../redux/slices/InsightsSlice';
import WorkspaceEmptyState from '../WorkspaceEmptyState';
import BioVsFinancialDashboard from './BioVsFinancialDashboard';
import {
  filterRowsByTimeRange,
  resolveFarmYearInputYears,
} from './computeFarmDiagnosticsMetrics';
import { resolveRotationScope } from 'pages/FarmsPage/Workspace/FarmDiagnostics/computeRotationEfficiency';
import { computeWueTrend } from './computeWueTrend';
import FarmDiagnosticsTable from './FarmDiagnosticsTable';
import FarmDiagnosticsTimeRangeControl from './FarmDiagnosticsTimeRangeControl';
import FinancialEfficiencyDashboard from './FinancialEfficiencyDashboard';
import RotationEfficiencyView from './RotationEfficiencyView';
import TrendDisplayOptionsControl, {
  DEFAULT_TREND_DISPLAY_OPTIONS,
  type TrendDisplayOptions,
} from './TrendDisplayOptionsControl';
import { useFarmDiagnosticsSeries } from './useFarmDiagnosticsSeries';
import WueTrendDashboard from './WueTrendDashboard';

const LENS_PURPOSE: Record<FarmDiagnosticsLens, string> = {
  [FARM_DIAGNOSTICS_LENS_ROTATION_EFFICIENCY]:
    VIEW_PURPOSE_DIAGNOSTICS_ROTATION_EFFICIENCY,
  [FARM_DIAGNOSTICS_LENS_WUE_TREND]: VIEW_PURPOSE_DIAGNOSTICS_WUE_TREND,
  [FARM_DIAGNOSTICS_LENS_FINANCIAL_EFFICIENCY]:
    VIEW_PURPOSE_DIAGNOSTICS_FINANCIAL_EFFICIENCY,
  [FARM_DIAGNOSTICS_LENS_BIO_VS_FINANCIAL]:
    VIEW_PURPOSE_DIAGNOSTICS_BIO_VS_FINANCIAL,
};

const LENS_OPTIONS: { value: FarmDiagnosticsLens; label: string }[] = [
  FARM_DIAGNOSTICS_LENS_ROTATION_EFFICIENCY,
  FARM_DIAGNOSTICS_LENS_WUE_TREND,
  FARM_DIAGNOSTICS_LENS_FINANCIAL_EFFICIENCY,
  FARM_DIAGNOSTICS_LENS_BIO_VS_FINANCIAL,
].map((value) => ({ value, label: FARM_DIAGNOSTICS_LENS_LABELS[value] }));

// RUE isn't a widely known term, so its lens explains it on hover (OP-2528).
const LENS_TOOLTIPS: Partial<Record<FarmDiagnosticsLens, string>> = {
  [FARM_DIAGNOSTICS_LENS_WUE_TREND]: FARM_DIAGNOSTICS_RUE_TOOLTIP,
};

// Passed straight through as CSS var() references rather than pre-resolved
// via getComputedStyle: this module can evaluate before tokens.css has been
// applied to the document (timing depends on chunk load order), in which
// case a getComputedStyle read at module scope permanently caches an empty
// string. AntD's CSS-in-JS just embeds whatever string it's given as the
// token's value, so a live var() reference is resolved correctly by the
// browser at paint time regardless of when this module ran. Mirrors
// Insights/index.tsx's own colorDarkGray/colorWhite consts exactly, since
// this reuses that same .insights-lens-segmented styling (see App.css).
const colorDarkGray = 'var(--color-dark-gray)';
const colorWhite = 'var(--color-white)';

// Stable identity for a paddock with no Plan/Actual choices yet, so the
// series memo isn't invalidated by a fresh {} on every render.
const NO_CATEGORY_SELECTION: Record<number, InputTaskCategory> = {};

interface FarmDiagnosticsViewProps {
  inputs: IInput[];
  farms: IFarm[];
  /** Already resolved to the tree's parent farm for Paddock/Plan selections, or null for Org/Crop — see useFarmsNavSync.ts. Only used by Rotation Efficiency. */
  selectedFarmId: string | null;
  orgName: string;
  paddockId?: string;
  paddockName?: string;
  farmId?: string;
  farmName?: string;
  gUnits: GUnit[];
}

/**
 * "Farm Diagnostics" tab — four lenses. Rotation Efficiency (Farm/Org-level
 * paddock comparison) needs no paddock selected in the tree and is always
 * reachable from the switcher. The other three (WUE Trend, Financial
 * Efficiency, Bio vs Financial) are per-paddock trends — when no paddock is
 * selected, an empty-state prompt takes the place of just that lens's
 * content, not the whole page. The Yearly Overview table (farm-level
 * rainfall/practice alongside this paddock's Plan/Actual and editable
 * yield/cost fields) is rendered once, collapsible, above whichever
 * per-paddock lens is active — it's the single data source those three
 * lenses read from via useFarmDiagnosticsSeries; Rotation Efficiency doesn't
 * use it (no single paddock to edit — see FarmYearInputsPanel instead,
 * which it renders per farm in scope). Takes the org-wide `inputs` list
 * (not the tree's year-filtered `scopedInputs`) since the whole point is to
 * look across every year the paddock has data for, regardless of the tree's
 * year filter.
 */
const FarmDiagnosticsView: React.FC<FarmDiagnosticsViewProps> = ({
  inputs,
  farms,
  selectedFarmId,
  orgName,
  paddockId,
  paddockName,
  farmId,
  farmName,
  gUnits,
}) => {
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const activeLens = useSelector(
    (state: RootState) => state.insightsReducer.farmDiagnosticsLens
  );
  const timeRange = useSelector(
    (state: RootState) => state.insightsReducer.farmDiagnosticsTimeRange
  );

  // Per paddock, in Redux, so the plan preview can be handed the same
  // choices (OP-2490).
  const selectedCategory = useSelector((state: RootState) =>
    paddockId
      ? state.insightsReducer.farmDiagnosticsCategory[paddockId]
      : undefined
  ) ?? NO_CATEGORY_SELECTION;
  const [trendDisplayOptions, setTrendDisplayOptions] = useState<TrendDisplayOptions>(
    DEFAULT_TREND_DISPLAY_OPTIONS
  );

  const rows = useMemo(
    () => (paddockId ? computeWueTrend(inputs, paddockId, gUnits) : []),
    [inputs, paddockId, gUnits]
  );

  const filteredRows = useMemo(
    () => filterRowsByTimeRange(rows, timeRange),
    [rows, timeRange]
  );

  const handleCategoryChange = (year: number, category: InputTaskCategory) => {
    if (!paddockId) return;
    dispatch(setFarmDiagnosticsCategory({ paddockId, year, category }));
  };

  // The Yearly Overview table below (its rainfall/practice, Plan/Actual selection,
  // and any yield/cost overrides) is the single data source for every
  // per-paddock lens's chart, including this hook's own derived series.
  const series = useFarmDiagnosticsSeries(
    filteredRows,
    paddockId,
    farmId,
    gUnits,
    selectedCategory
  );

  // Year range for the Yearly Overview table's rainfall/practice columns —
  // the current time window applied to this paddock's full history, then
  // contiguous-filled so a true gap year (e.g. Fallow) can still be tagged.
  // Goes through the same shared helper Rotation Efficiency uses (single
  // paddock here) — this is also what makes the row set a strict superset
  // of `filteredRows`, so every Epic 1 row always has a matching year here.
  const farmYearInputYears = useMemo(
    () => resolveFarmYearInputYears([rows], timeRange),
    [rows, timeRange]
  );

  // Same rule as the per-paddock lenses below: an empty state fills the
  // workspace, only populated content is capped at 100rem.
  const hasRotationPaddocks = useMemo(
    () =>
      resolveRotationScope(farms, selectedFarmId, orgName, gUnits)
        .paddocks.length > 0,
    [farms, selectedFarmId, orgName, gUnits]
  );

  const isRotationEfficiency =
    activeLens === FARM_DIAGNOSTICS_LENS_ROTATION_EFFICIENCY;

  const lensSwitcher = (
    <ConfigProvider
      theme={{
        components: {
          Segmented: {
            trackBg: 'transparent',
            trackPadding: 2,
            itemSelectedBg: colorDarkGray,
            itemSelectedColor: colorWhite,
          },
        },
      }}
    >
      <Segmented<FarmDiagnosticsLens>
        className="insights-lens-segmented w-fit"
        options={LENS_OPTIONS.map((opt) => ({
          value: opt.value,
          label: (
            <span
              style={
                activeLens === opt.value
                  ? { color: colorWhite, fontWeight: 600 }
                  : undefined
              }
            >
              <TermTooltip tooltip={LENS_TOOLTIPS[opt.value]}>
                {opt.label}
              </TermTooltip>
            </span>
          ),
        }))}
        value={activeLens}
        onChange={(value) =>
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(FARMS_URL_PARAM_DIAGNOSTICS_LENS, value);
            return next;
          })
        }
      />
    </ConfigProvider>
  );

  const viewHeader = (
    <ViewHeader
      purpose={LENS_PURPOSE[activeLens]}
    />
  );

  if (isRotationEfficiency) {
    return (
      <div
        className={`flex min-w-0 flex-1 flex-col gap-6 overflow-y-auto pr-4 ${hasRotationPaddocks ? 'max-w-[100rem]' : ''}`}
      >
        {lensSwitcher}
        {viewHeader}
        <RotationEfficiencyView
          inputs={inputs}
          farms={farms}
          selectedFarmId={selectedFarmId}
          orgName={orgName}
          gUnits={gUnits}
        />
      </div>
    );
  }

  // The empty state fills the workspace like the Insights lenses' do; only
  // the populated view is capped at 100rem for readable tables and charts.
  const isEmpty = !paddockId || rows.length === 0;

  return (
    <div
      className={`flex min-w-0 flex-1 flex-col gap-6 overflow-y-auto pr-4 ${isEmpty ? '' : 'max-w-[100rem]'}`}
    >
      {lensSwitcher}
      {viewHeader}

      {!paddockId ? (
        <WorkspaceEmptyState message={FARM_DIAGNOSTICS_NO_PADDOCK_MESSAGE} />
      ) : rows.length === 0 ? (
        <WorkspaceEmptyState message={FARM_DIAGNOSTICS_NO_DATA_MESSAGE} />
      ) : (
        <>
          <FarmDiagnosticsTimeRangeControl
            minYear={rows[0].year}
            maxYear={rows[rows.length - 1].year}
            value={timeRange}
            onChange={(value) => dispatch(setFarmDiagnosticsTimeRange(value))}
          />

          {/* Single data source for every lens below — its farm-level rainfall/practice, Plan/Actual selection, and any yield/cost overrides all drive useFarmDiagnosticsSeries directly. Rendered before the lens content (not after) so it reads as shared configuration, not something that belongs to whichever lens happens to be active. The "Displaying: {paddockName}" label lives in its own header row above the paddock-scoped columns, since the farm-level columns beside them apply regardless of which paddock is selected. */}
          {farmId && (
            <FarmDiagnosticsTable
              paddockId={paddockId}
              paddockName={paddockName}
              farmId={farmId}
              farmName={farmName}
              rows={filteredRows}
              years={farmYearInputYears}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
            />
          )}

          <TrendDisplayOptionsControl
            options={trendDisplayOptions}
            onChange={setTrendDisplayOptions}
          />

          {activeLens === FARM_DIAGNOSTICS_LENS_WUE_TREND && (
            <WueTrendDashboard series={series} {...trendDisplayOptions} />
          )}

          {activeLens === FARM_DIAGNOSTICS_LENS_FINANCIAL_EFFICIENCY && (
            <FinancialEfficiencyDashboard series={series} {...trendDisplayOptions} />
          )}

          {activeLens === FARM_DIAGNOSTICS_LENS_BIO_VS_FINANCIAL && (
            <BioVsFinancialDashboard series={series} {...trendDisplayOptions} />
          )}
        </>
      )}
    </div>
  );
};

export default FarmDiagnosticsView;
